import { Field } from "../../../../../../../../settings/types/field";

const startingSpinTag = '{spin}';
const endingSpinTag = '{endspin}';

const FALLBACK_MERGE_TAG_REGEX = /\{\{[\s\S]*?\}\}/g;

/**
 * Builds merge tag search regex using the same pattern as content.service.ts prepareSearchString
 * Pattern: {{\s*key\s*(?:\|\s*['"](.*)['"])?\s*}}
 */
const buildMergeTagRegex = (keys: string[]): RegExp => {
  const searchString = keys
    .map(
      (key) =>
        `{{\\s*${key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*` +
        `(?:\\|\\s*['"](.*?)['"]\\s*)?}}`,
    )
    .join('|');
  return new RegExp(searchString, 'gis');
};

// Type for storing original merge tag values
type MergeTagStore = Record<string, string>;

/**
 * Extracts all merge tags from text and replaces them with placeholders
 * Stores original values in an object for later restoration
 * @param text - The text to process
 * @param keys - Optional array of merge tag keys (e.g., ['firstName', 'lastName'])
 *               If provided, uses the same pattern as content.service.ts prepareSearchRegex
 *               If not provided, matches any {{...}} pattern
 * @returns Object with protected text and store of original values
 */
const protectMergeTags = (
  text: string,
  keys?: string[],
): { protectedText: string; mergeTagStore: MergeTagStore } => {
  const mergeTagStore: MergeTagStore = {};
  let index = 0;
  // Use specific regex if keys provided, otherwise fallback to generic pattern
  const regex =
    keys && keys.length > 0
      ? buildMergeTagRegex(keys)
      : FALLBACK_MERGE_TAG_REGEX;

  const protectedText = text.replace(regex, (match) => {
    const placeholder = `{{__SH_PROTECTED_${index}__}}`;
    mergeTagStore[placeholder] = match; // Store original value
    index += 1;
    return placeholder;
  });

  return { protectedText, mergeTagStore };
};

/**
 * Restores the original merge tags from placeholders using the stored values
 * @param text - Text with placeholders
 * @param mergeTagStore - Object containing placeholder -> original value mapping
 */
const restoreMergeTags = (
  text: string,
  mergeTagStore: MergeTagStore,
): string => {
  let restoredText = text;
  // Replace each placeholder with its original value from store
  Object.entries(mergeTagStore).forEach(([placeholder, originalValue]) => {
    restoredText = restoredText.replace(placeholder, originalValue);
  });
  return restoredText;
};

const getPosition = (
  string: string,
  subString: string,
  startFromIndex: number,
) =>
  string
    .substring(startFromIndex)
    .toLowerCase()
    .split(subString, 1)
    .join(subString).length + startFromIndex;

const getSpinText = (
  startingIndex: number,
  endingIndex: number,
  string: string,
) => string.slice(startingIndex, endingIndex);

const getRandomElement = (inputArray: string[]) =>
  Math.floor(Math.random() * inputArray.length);

const replaceSpinTagsWithText = (
  fullString: string,
  startIndex: number,
  endIndex: number,
  text: string,
) => {
  const trimmedText = text.trim();
  const beforeTextIndex =
    fullString.substring(0, startIndex).length + trimmedText.length;
  const clonedText =
    fullString.substring(0, startIndex) +
    trimmedText +
    fullString.substring(endIndex);
  return { clonedText, stoppedAtIndex: beforeTextIndex };
};

/**
 * Internal function that processes spin tax without merge tag protection
 */
const processSpinTaxInternal = (spinText: string) => {
  const startingSpinTagsCount = (spinText.match(/{spin}/gi) || []).length;

  let clonedSpinText = spinText.valueOf();

  let lastStartingSpinIndex = 0;

  for (let i = 1; i <= startingSpinTagsCount; i += 1) {
    const startingPosition = getPosition(
      clonedSpinText,
      startingSpinTag,
      lastStartingSpinIndex,
    );

    const endingPosition = getPosition(
      clonedSpinText,
      endingSpinTag,
      startingPosition,
    );

    if (
      startingPosition !== endingPosition &&
      clonedSpinText.length - endingSpinTag.length >= endingPosition
    ) {
      const unParsedSpinText = getSpinText(
        startingPosition + startingSpinTag.length,
        endingPosition,
        clonedSpinText,
      );

      const parsedSpinText = unParsedSpinText.split('|');
      const randomizedIndex = getRandomElement(parsedSpinText);

      const randomizedText = parsedSpinText[randomizedIndex];

      const { clonedText, stoppedAtIndex } = replaceSpinTagsWithText(
        clonedSpinText,
        startingPosition,
        endingPosition + endingSpinTag.length,
        randomizedText,
      );

      clonedSpinText = clonedText;
      lastStartingSpinIndex = stoppedAtIndex;
    }
  }

  return clonedSpinText;
};

/**
 * Parses spin tax while protecting merge tags from being affected by pipe characters
 * This prevents merge tags like {{name|'fallback'}} or merge tag values containing pipes
 * from being broken by spin tax parsing
 *
 * @param spinText - The text containing spin tax syntax
 * @param mergeTagKeys - Optional array of merge tag keys (e.g., ['firstName', 'lastName'])
 *                       If provided, uses the same regex pattern as content.service.ts
 *                       If not provided, matches any {{...}} pattern
 */
export const getParsedSpinText = (
  spinText: string,
  mergeTagKeys?: string[],
  mergeTags?: Field[],
) => {
  const defaultMergeTagKeys = mergeTags?.map((tag) => tag.label.toLowerCase());
  // Step 1: Protect merge tags by replacing them with placeholders
  // Store original values in object: { '{{__SH_PROTECTED_0__}}': '{{firstName|'hey'}}' }
  const { protectedText, mergeTagStore } = protectMergeTags(
    spinText,
    Array.from(new Set([...(mergeTagKeys || []), ...(defaultMergeTagKeys || [])])),
  );

  // Step 2: Process spin tax on the protected text
  const processedText = processSpinTaxInternal(protectedText);

  // Step 3: Restore original merge tags from store
  // If a placeholder was removed during spin selection, it won't be in the text to replace
  return restoreMergeTags(processedText, mergeTagStore);
};
